'use strict';


import { BaseElement } from '../interfaces/BaseElement';
import {isMobile, settingsData, UIRoot} from '../../settings';
import { zoom } from '../../libs/directive';
import { ShareBtn } from '../components/share/Share';
import { ResizeObserver } from 'resize-observer';
import { getCurrentLangDirection } from '../components/langSwitcher/langSwitcher';
import { Drag } from '../interfaces/Drag';
import printJS from "print-js";
import { Cloudflare } from '../api/Cloudflare';
import {checkImageExists} from "../../functions";


const ICONS = {
	lineWidth: (size,index) => `<svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M3 16.5L9 10L13 16L21 6.5"  stroke-width="${index + 1}" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>`,
	eraseSize: (size,index) => `<svg width="${(index + 1) * 10}px" height="${(index + 1) * 10}px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>`,
	fontSize: (size,index) => `<svg width="${(index + 1) * 10}px" height="${(index + 1) * 10}px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 3V21M9 21H15M19 6V3H5V6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
};


export class Paint {
	constructor (url) {
		this.container = new BaseElement()
			.addClass('paint')
			.appendTo(UIRoot)
			.DOMElement;

		new BaseElement('button')
			.addClass('paint__close-btn','icon-close')
			.appendTo(this.container)
			.event('click',() => this.container.remove());

		this.dashboard = new BaseElement()
			.addClass('paint__dashboard','zoom','disabled-drag')
			.event('wheel',this.#scale.bind(this))
			.event('touchend',this.#scale.bind(this))
			.appendTo(this.container)
			.DOMElement;

		this.observer = new ResizeObserver((e) => this.#resize(e));

		this.transformContainer = new BaseElement()
			.addClass('paint__dashboard-transform')
			.appendTo(this.dashboard)
			.DOMElement;

		this.layers = new BaseElement()
			.addClass('paint__dashboard-layers')
			.appendTo(this.transformContainer)
			.DOMElement;

		this.width = null;
		this.height = null;
		this.drawPanel = {};
		this.aspect = {
			image: 1,
			text: 1,
			scale: 1,
			dashboard: 1
		};
		this.panel = null;
		this.mode = null;
		this.activeElement = null;
		this.settings = {
			colors: ['#000000','#ffffff','#ff0000','#00ff00','#0000ff','#f6ff00','#ff9500','#9c9c9c'],
			lineWidth: 10,
			fontSize: 64,
			color: '#0000ff',
			eraseSize: 20,
			lineWidths: [5,10,15],
			eraseSizes: [10,20,30],
			fontSizes: [32,64,128],
			share: [
				{
					"type" : "whatsapp",
					"icon" : "icon-whatsapp"
				},
				{
					"type" : "telegram",
					"icon" : "icon-social-telegram"
				},
				{
					"type" : "skype",
					"icon" : "icon-social-skype"
				}
			]
		};

		this.canvas = {
			main: null,
			draw: null,
			merge: null
		};

		this.context = {
			main: null,
			draw: null
		};

		this.image = new Image();
		this.image.setAttribute('crossorigin', 'anonymous');
		this.image.src = url;
		this.name = getFileName(url);
		this.image.addEventListener('load',() => {
			this.width = this.image.width;
			this.height = this.image.height;
			this.createCanvas();
			this.createDrawPanel();
			this.createSavePanel();
			zoom();
			this.observer.observe(this.dashboard);
		});
	}

	createCanvas() {
		this.canvas.main = new BaseElement('canvas')
			.addClass('paint__canvas','paint__main-canvas')
			.appendTo(this.layers)
			.DOMElement;

		this.canvas.draw = new BaseElement('canvas')
			.addClass('paint__canvas','paint__draw-canvas')
			.appendTo(this.layers)
			.DOMElement;

		this.canvas.main.width = this.width;
		this.canvas.main.height = this.height;
		this.canvas.draw.width = this.width;
		this.canvas.draw.height = this.height;

		this.context.main = this.canvas.main.getContext('2d');
		this.context.main.fillStyle = "rgba(255, 255,255, 1)";
		this.context.main.fillRect(0, 0, this.width, this.height);
		this.context.draw = this.canvas.draw.getContext('2d');
		this.context.main.drawImage(this.image,0,0,this.width,this.height);
	}

	createDrawPanel() {
		this.panel = new BaseElement()
			.addClass('paint__draw-panel')
			.event('click',this.#drawPanelHandler.bind(this))
			.appendTo(this.container)
			.DOMElement;

		const wrapper = new BaseElement()
			.addClass('paint__draw-panel-wrapper')
			.event('scroll',this.#updateSettingsPositions.bind(this))
			.appendTo(this.panel)
			.DOMElement;

		this.drawPanel.move = new Move(wrapper,this);
		this.drawPanel.palette = new Palette(wrapper,this);
		this.drawPanel.pencil = new Pencil(wrapper,this);
		if (!isMobile) {
			this.drawPanel.text = new Text(wrapper,this);
		}
		this.drawPanel.eraser = new Eraser(wrapper,this);
		this.drawPanel.clear = new Clear(wrapper,this);
		this.drawPanel.history = new History(wrapper,this);

		this.drawPanel.pencil.active = true;
		this.activeElement = this.drawPanel.pencil;
	}

	createSavePanel() {
		const container = new BaseElement()
			.addClass('paint__save-panel')
			.appendTo(isMobile ? this.panel : this.container)
			.DOMElement;

		new BaseElement()
			.addClass('paint__icon','icon-download')
			.event('click',this.#download.bind(this))
			.appendTo(container);

		new BaseElement()
			.addClass('paint__icon','icon-share')
			.event('click',this.#share.bind(this))
			.appendTo(container);

		!isMobile && new BaseElement()
			.addClass('paint__icon','icon-print')
			.event('click',this.#print.bind(this))
			.appendTo(container);
	}

	#scale() {
		setTimeout(() => {
			this.aspect.scale = Number(this.transformContainer.getAttribute('data-scale'));
			this.drawPanel.move.updateState();
		},0);
	}

	#resize(e) {
		const dashboard = e[0].contentRect;
		this.aspect.image = this.width / this.height;
		this.aspect.dashboard = dashboard.width / dashboard.height;

		if (this.aspect.image > this.aspect.dashboard) {
			const value = dashboard.height / (this.aspect.image / this.aspect.dashboard);
			this.layers.style.height = value + 'px';
			this.layers.style.width = '100%';
			this.aspect.text = this.height / value;
		} else {
			const value = dashboard.width / (this.aspect.dashboard / this.aspect.image);
			this.layers.style.height = '100%';
			this.layers.style.width = value + 'px';
			this.aspect.text = this.width / value;
		}
		this.#updateSettingsPositions();
		this.#scale();
	}

	#updateSettingsPositions() {
		Object.values(this.drawPanel).forEach(element => {
			if (element.updateSettingsPosition) {
				element.updateSettingsPosition();
			}
		});
	}

	async #download() {
		await this.#mergeCanvases();
		const link = document.createElement('a');
		link.href = this.canvas.merge.toDataURL();
		link.download = `${this.name ? this.name + '_modified' : 'paint'}.png`;
		link.click();
	}

	async #print() {
		await this.#mergeCanvases();
		const {cdnRepository,projectFolder} = config;
		const logoUrl = `${cdnRepository}/${projectFolder}/resources/img/logo.png`;
		const imageExists = await checkImageExists(logoUrl);
		const logoUrlResized = imageExists ? Cloudflare.resize(logoUrl, { height: 100 }) : '';
		
		printJS({
			printable: this.canvas.merge.toDataURL(),
			type: 'image',
			header: `<div class="printjs-custom-header"><h3>${config.features?.printHeader?.title || ''}</h3>${imageExists ? `<img src="${logoUrlResized}" alt="Logo" />` : ''}</div>`,
			style: '.printjs-custom-header { display: flex; flex-direction: row; justify-content: flex-end; align-items: center; gap: 20px; font-family: "Arial"; margin-bottom: 60px}'
		});
	}

	#mergeCanvases() {
		return new Promise(async mainResolve => {
			this.canvas.merge = new BaseElement('canvas').DOMElement;
			this.canvas.merge.width = this.width;
			this.canvas.merge.height = this.height;
			const ctx = this.canvas.merge.getContext('2d');

			const copyImage = (canvas) => {
				return new Promise(resolve => {
					const image = new Image();
					image.onload = () => {
						ctx.drawImage(image,0,0,this.width,this.height);
						resolve();
					};
					image.src = canvas.toDataURL();
				})
			};

			await copyImage(this.canvas.main);
			await copyImage(this.canvas.draw);
			mainResolve();
		});
	}

	async #share() {
		await this.#mergeCanvases();
		const response = await fetch(this.canvas.merge.toDataURL());
		const blob = await response.blob();

		const shareData = {
			files: [
				new File([blob],`${this.name ? this.name + '_modified' : 'paint'}.png`,{
					type: 'image/png',
					lastModified: new Date().getTime()
				})
			]
		};

		if (navigator.canShare && navigator.canShare(shareData)) {
			navigator.share(shareData);
		}

		// const mediaContainer = new BaseElement()
		// 	.addClass('social-media','paint__share')
		// 	.appendTo(UIRoot)
		// 	.DOMElement;
		//
		// const content = new BaseElement()
		// 	.addClass('social-media__content')
		// 	.appendTo(mediaContainer)
		// 	.DOMElement;
		//
		// const closeBtn = new BaseElement('button')
		// 	.addClass('social-media__close-btn','icon-close')
		// 	.event('click',() => mediaContainer.remove())
		// 	.appendTo(mediaContainer);
		//
		//
		// this.settings.share.forEach(social => new ShareBtn(content,{
		// 	type: social.type,
		// 	url: this.canvas.merge.toDataURL()
		// },social.icon));
	}

	#drawPanelHandler(e) {
		const mode = e.target.getAttribute('data-mode');
		const disabled = e.target.getAttribute('data-disabled');
		if (mode && !disabled) {
			if (this.activeElement) {
				if (this.activeElement === this.drawPanel[mode]) return;
				this.activeElement.active = false;
			}
			this.mode = mode;
			this.drawPanel[mode].active = true;
			this.activeElement = this.drawPanel[mode];
			this.container.setAttribute('data-selected-mode',mode);
		}
	}

}

class DrawElement {
	constructor () {
		this.enabled = false;
	}

	onChange() {
		if (this.enabled) {
			this.btn.addClass('--active-mode');
		} else {
			this.btn.removeClass('--active-mode');
		}
	}

	updateSettingsPosition() {
		if (this.settings) {
			const btnBounds = this.btn.DOMElement.getBoundingClientRect();
			const panelBounds = this.scope.panel.getBoundingClientRect();
			const settings = this.btn.DOMElement.querySelector('.paint__settings__container-wrapper');
			settings.style.top = btnBounds.top - panelBounds.top + 'px';
		}
	}

	get active() {
		return this.enabled;
	}

	set active(state) {
		this.enabled = state;
		this.onChange();
	}
}

class Move extends DrawElement{
	constructor (parent,scope) {
		super();
		this.scope = scope;
		this.disabled = true;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-hand','paint--disabled')
			.addAttribute('data-mode','move')
			.appendTo(parent);
	}

	updateState() {
		if (this.scope.aspect.scale > 1) {
			this.btn.removeClass('paint--disabled');
			this.btn.deleteAttribute('data-disabled');
			this.disabled = false;
		} else {
			this.btn.addClass('paint--disabled');
			this.btn.addAttribute('data-disabled',true);
			this.disabled = true;
			if (this.active) {
				this.active = false;
				this.scope.activeElement = null;
			}
		}
	}

	onChange () {
		super.onChange();
		if (this.active && !this.disabled) {
			this.scope.dashboard.classList.remove('disabled-drag');
		} else {
			this.scope.dashboard.classList.add('disabled-drag');
		}
	}

}

class Palette extends DrawElement {
	constructor (parent,scope) {
		super();
		this.scope = scope;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-palette','paint__palette')
			.event('click',this.openPalette.bind(this))
			//.addAttribute('data-mode','palette')
			.appendTo(parent);

		this.selectedColorHelper = new BaseElement()
			.addClass('paint__palette__helper')
			.appendTo(this.btn.DOMElement);

		const colors = new BaseElement()
			.addClass('paint__settings__container-wrapper')
			.appendTo(this.btn.DOMElement)
			.DOMElement;

		this.settings = new BaseElement()
			.addClass('paint__settings__container','hidden')
			.appendTo(colors)
			.event('click',this.#colorSelectHandler.bind(this));

		this.scope.settings.colors.forEach(color => {
			new BaseElement()
				.addClass('paint__palette__color')
				.addAttribute('data-color',color)
				.addStyle('background',color)
				.appendTo(this.settings.DOMElement)

			if (color === this.scope.settings.color) {
				this.selectedColorHelper.addStyle('background',color);
			}
		});

		this.scope.container.addEventListener('click',(e) => {
			if (!this.settings.DOMElement.classList.contains('hidden')) {
				this.openPalette(e);
			}
		});

	}

	#colorSelectHandler(e) {
		const color = e.target.getAttribute('data-color');
		if (color) {
			this.selectedColorHelper.addStyle('background',color);
			this.scope.settings.color = color;
			this.scope.drawPanel.text?.updateViewFontSize();
		}
	}

	openPalette(e) {
		e.stopPropagation();
		this.updateSettingsPosition();
		this.settings.toggleClass('hidden');
	}
}

class Pencil extends DrawElement {
	constructor (parent,scope) {
		super();
		this.scope = scope;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-pencil')
			.event('click',this.#openSettings.bind(this))
			.addAttribute('data-mode','pencil')
			.appendTo(parent);

		const data = createSettings(
			'pencil',
			this.scope.settings.lineWidths,
			this.scope.settings.lineWidth,
			ICONS.lineWidth,
			(value) => {
				this.scope.settings.lineWidth = value;
				this.#closeSettings();
			}
			);

		this.btn.appendElement(data.wrapper);
		this.settings = data.settings;


		this.drawHandler = (e) => this.#draw(e);

		this.coord = {
			x: 0,
			y: 0
		};
		this.bounding = null;

		this.scope.canvas.draw.addEventListener('mousedown',this.#startDraw.bind(this));
		this.scope.canvas.draw.addEventListener('touchstart',this.#startDraw.bind(this));
		this.scope.canvas.draw.addEventListener('mouseup',this.#stopDraw.bind(this));
		this.scope.canvas.draw.addEventListener('touchend',this.#stopDraw.bind(this));
	}

	#openSettings() {
		this.updateSettingsPosition();
		this.settings.removeClass('hidden');
	}

	onChange () {
		super.onChange();
		if (!this.active) {
			this.#closeSettings();
		}
	}

	#closeSettings() {
		this.settings.addClass('hidden');
	}

	#startDraw(e) {
		this.#closeSettings();
		if (this.active) {
			if (e.touches && e.touches.length === 2) return;
			this.scope.canvas.draw.addEventListener('mousemove',this.drawHandler);
			this.scope.canvas.draw.addEventListener('touchmove',this.drawHandler);
			this.#reposition(e);
			this.scope.drawPanel.history.execute(new CanvasDrawCommand(this.scope.canvas.draw,this.scope.context.draw));
		}
	}

	#draw(e) {
		if (e.touches && e.touches.length === 2) return;
		this.scope.context.draw.beginPath();
		this.scope.context.draw.lineWidth = this.scope.settings.lineWidth;
		this.scope.context.draw.lineCap = "round";
		this.scope.context.draw.strokeStyle = this.scope.settings.color;
		this.scope.context.draw.moveTo(this.coord.x, this.coord.y);
		this.#reposition(e);
		this.scope.context.draw.lineTo(this.coord.x, this.coord.y);
		this.scope.context.draw.stroke();
	}

	#stopDraw() {
		this.scope.canvas.draw.removeEventListener('mousemove',this.drawHandler);
		this.scope.canvas.draw.removeEventListener('touchmove',this.drawHandler);
	}

	#reposition (e) {
		const x = e.touches ? e.touches[0].clientX : e.clientX;
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		this.bounding = this.scope.canvas.draw.getBoundingClientRect();
		this.coord.x = (x - this.bounding.left) / this.bounding.width  * this.scope.width;
		this.coord.y = (y - this.bounding.top) / this.bounding.height * this.scope.height;
	}
}

class Text extends DrawElement {
	constructor (parent,scope) {
		super();
		this.scope = scope;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-text-paragraph')
			.event('click',this.#openSettings.bind(this))
			.addAttribute('data-mode','text')
			.appendTo(parent);

		this.edit = false;
		this.textContainer = new BaseElement()
			.addClass('paint__text-block')
			.DOMElement;
		const header = new BaseElement()
			.addClass('paint__text-block__header')
			.appendTo(this.textContainer)
			.DOMElement;
		new BaseElement('button')
			.addClass('paint__text-btn','paint__save-btn','icon-check-mark')
			.event('pointerdown',this.drawText.bind(this))
			.appendTo(header);
		new BaseElement('button')
			.addClass('paint__text-btn','paint__cancel-btn','icon-close')
			.event('pointerdown',this.removeText.bind(this))
			.appendTo(header);
		this.textArea = new BaseElement('textarea')
			.addStyle('font-family','Arial')
			.addClass('paint__text-area')
			.appendTo(this.textContainer)
			.DOMElement;

		this.textResize = new BaseElement()
			.addClass('paint__text-resize')
			.appendTo(this.textContainer)
			.event('mousedown',this.#resizeStart.bind(this))
			.event('mouseup',this.#resizeEnd.bind(this))
			.event('touchstart',this.#resizeStart.bind(this))
			.event('touchend',this.#resizeEnd.bind(this));

		const data = createSettings(
			'fontSize',
			this.scope.settings.fontSizes,
			this.scope.settings.fontSize,
			ICONS.fontSize,
			(value) => {
				this.scope.settings.fontSize = value;
				this.updateViewFontSize();
				this.#closeSettings();
			}
		);

		this.resizeMoveHandler = (e) => this.#resizeMove(e);
		this.btn.appendElement(data.wrapper);
		this.settings = data.settings;

		this.coord = {
			x: 0,
			y: 0
		};

		this.resizeData = {
			start: {},
			end: {}
		};

		this.drag = new Drag(this.textContainer, {
			updateElementPositionAfterDragEnd: false,
			touchSupport: true
		});
		this.drag.onDragStart = (e) => this.#dragStart(e);
		this.drag.onDragEnd = (e) => this.#dragOver(e);

		this.scope.canvas.draw.addEventListener('mousedown',this.#startPrint.bind(this));
		this.scope.canvas.draw.addEventListener('touchstart',this.#startPrint.bind(this));
	}

	#resizeEnd() {
		document.removeEventListener('mousemove',this.resizeMoveHandler.bind(this));
		document.removeEventListener('touchmove',this.resizeMoveHandler.bind(this));
		this.resizeData = {
			start: {},
			end: {}
		};
	}

	#resizeStart(e) {
		e.stopPropagation();
		this.resizeData.start.x = e.touches ? e.touches[0].clientX : e.clientX;
		this.resizeData.start.y = e.touches ? e.touches[0].clientY : e.clientY;
		const bounds = this.textArea.getBoundingClientRect();
		this.resizeData.start.width = bounds.width;
		this.resizeData.start.height = bounds.height;
		document.addEventListener('mousemove',this.resizeMoveHandler.bind(this));
		document.addEventListener('touchmove',this.resizeMoveHandler.bind(this));
	}

	#resizeMove(e) {
		this.resizeData.end.x = e.touches ? e.touches[0].clientX : e.clientX;
		this.resizeData.end.y = e.touches ? e.touches[0].clientY : e.clientY;
		let x = null;
		if (getCurrentLangDirection() === 'rtl') {
			x = this.resizeData.start.width - this.resizeData.end.x + this.resizeData.start.x;
		} else {
			x = this.resizeData.start.width + this.resizeData.end.x - this.resizeData.start.x;
		}
		const y = this.resizeData.start.height + this.resizeData.end.y - this.resizeData.start.y;
		this.textArea.style.width =  x + 'px';
		this.textArea.style.height = y + 'px';
	}

	#dragStart(e) {
		const bounds = this.textContainer.getBoundingClientRect();
		const x = e.touches ? e.touches[0].clientX : e.clientX;
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		this.dragData = {
			x: x - bounds.x,
			y: y - bounds.y,
			width: bounds.width
		};
	}

	#dragOver(e) {
		const x = e.touches ? e.changedTouches[0].clientX : e.clientX;
		const y = e.touches ? e.changedTouches[0].clientY : e.clientY;
		const dir = getCurrentLangDirection();
		const evt = {
			clientX: dir === 'rtl' ? (x - this.dragData.x + this.dragData.width) : x - this.dragData.x,
			clientY: y - this.dragData.y
		};
		this.#reposition(evt);
	}

	#openSettings() {
		this.updateSettingsPosition();
		this.settings.removeClass('hidden');
	}

	#closeSettings() {
		this.settings.addClass('hidden');
	}

	onChange () {
		super.onChange();
		if (this.edit) {
			this.removeText();
		}
		if (!this.active) {
			this.#closeSettings();
		}
	}

	drawText() {
		const text = this.textArea.value;
		this.removeText();
		this.scope.drawPanel.history.execute(new CanvasDrawCommand(this.scope.canvas.draw,this.scope.context.draw));
		this.wrapText(text,this.coord.x,this.coord.y + this.scope.settings.fontSize);
	}

	removeText() {
		this.textContainer.remove();
		this.textArea.value = '';
		this.edit = false;
		this.scope.dashboard.classList.remove('disabled-zoom');
	}

	updateViewFontSize() {
		if (this.active) {
			this.textArea.style.fontSize = this.scope.settings.fontSize / this.scope.aspect.text * this.scope.aspect.scale + 'px';
			this.textArea.style.color = this.scope.settings.color;
			this.#clampTextarea();
			this.textArea.focus();
		}
	}

	#startPrint(e) {
		this.#closeSettings();
		if (this.active) {
			if (this.edit) {
				this.drawText();
			}
			if (e.touches && e.touches.length === 2) return;
			this.scope.dashboard.classList.add('disabled-zoom');
			this.textContainer.style.transform = '';
			this.drag.xOffset = 0;
			this.drag.yOffset = 0;
			const x = e.touches ? e.touches[0].clientX : e.clientX;
			const y = e.touches ? e.touches[0].clientY : e.clientY;
			this.#reposition(e);

			if (getCurrentLangDirection() === 'rtl') {
				this.textContainer.style.right = window.innerWidth - x + 'px';
			} else {
				this.textContainer.style.left = x + 'px';
			}
			this.textContainer.style.top = y + 'px';
			this.scope.container.appendChild(this.textContainer);
			this.textArea.focus();
			this.edit = true;
			this.updateViewFontSize();
		}
	}

	#clampTextarea() {
		const bounds = this.textArea.getBoundingClientRect();
		if (bounds.x + bounds.width > window.innerWidth) {
			this.textArea.style.width = (window.innerWidth - bounds.x) * 0.85 + 'px';
		}
	}

	#reposition (e) {
		const x = e.touches ? e.touches[0].clientX : e.clientX;
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		this.bounding = this.scope.canvas.draw.getBoundingClientRect();
		this.coord.x = (x - this.bounding.left) / this.bounding.width  * this.scope.width;
		this.coord.y = (y - this.bounding.top) / this.bounding.height * this.scope.height;
	}

	wrapText(text, x, y) {
		this.scope.context.draw.font = `${this.scope.settings.fontSize}px Arial`;
		this.scope.context.draw.fillStyle = `${this.scope.settings.color}`;
		this.scope.context.draw.direction = getCurrentLangDirection();
		const lines = text.split("\n");
		for (let i = 0; i < lines.length; i++) {
			this.scope.context.draw.fillText(lines[i], x, y);
			y += this.scope.settings.fontSize;
		}
	}
}

class Eraser extends DrawElement {
	constructor (parent,scope) {
		super();
		this.scope = scope;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-erase')
			.event('click',this.#openSettings.bind(this))
			.addAttribute('data-mode','eraser')
			.appendTo(parent);

		this.canvas = new BaseElement('canvas')
			.addClass('paint__canvas','paint__eraseHelper')
			.DOMElement;
		this.canvas.width = this.scope.width;
		this.canvas.height = this.scope.height;
		this.canvas.style.pointerEvents = 'none';
		this.canvas.style.zIndex = 10;

		this.ctx = this.canvas.getContext('2d');

		const data = createSettings(
			'erase',
			this.scope.settings.eraseSizes,
			this.scope.settings.eraseSize,
			ICONS.eraseSize,
			(value) => {
				this.scope.settings.eraseSize = value;
				this.#closeSettings();
			}
		);

		this.btn.appendElement(data.wrapper);
		this.settings = data.settings;

		this.eraseHandler = (e) => this.#erase(e);
		this.eraseHelperHandler = (e) => this.#updateHelper(e);

		this.coord = {
			x: 0,
			y: 0
		};
		this.bounding = null;

		this.scope.canvas.draw.addEventListener('mousedown',this.#startErase.bind(this));
		this.scope.canvas.draw.addEventListener('touchstart',this.#startErase.bind(this));
		this.scope.canvas.draw.addEventListener('mouseup',this.#stopErase.bind(this));
		this.scope.canvas.draw.addEventListener('touchend',this.#stopErase.bind(this));
	}

	#openSettings() {
		this.updateSettingsPosition();
		this.settings.removeClass('hidden');
	}

	#closeSettings() {
		this.settings.addClass('hidden');
	}

	onChange () {
		super.onChange();
		if (!this.active) {
			this.#closeSettings();
		}
	}

	#updateHelper(e) {
		this.ctx.clearRect(0,0,this.scope.width,this.scope.height);
		this.#reposition(e);
		this.ctx.beginPath();
		this.ctx.arc(this.coord.x, this.coord.y, this.scope.settings.eraseSize, 0, 2 * Math.PI);
		this.ctx.stroke();
	}

	#startErase(e) {
		this.#closeSettings();
		if (this.active) {
			if (e.touches && e.touches.length === 2) return;
			this.scope.canvas.draw.addEventListener('mousemove',this.eraseHandler);
			this.scope.canvas.draw.addEventListener('touchmove',this.eraseHandler);
			this.#reposition(e);
			this.scope.drawPanel.history.execute(new CanvasDrawCommand(this.scope.canvas.draw,this.scope.context.draw));
			this.#erase(e);
		}
	}

	#erase(e) {
		if (e.touches && e.touches.length === 2) return;
		this.scope.context.draw.save();
		this.scope.context.draw.beginPath();
		this.scope.context.draw.arc(this.coord.x, this.coord.y, this.scope.settings.eraseSize, 0, 2*Math.PI, true);
		this.scope.context.draw.clip();
		this.scope.context.draw.clearRect(this.coord.x - this.scope.settings.eraseSize,this.coord.y-this.scope.settings.eraseSize,this.scope.settings.eraseSize*2,this.scope.settings.eraseSize*2);
		this.scope.context.draw.restore();
		this.#reposition(e);
	}

	#stopErase() {
		this.scope.canvas.draw.removeEventListener('mousemove',this.eraseHandler);
		this.scope.canvas.draw.removeEventListener('touchmove',this.eraseHandler);
	}

	#reposition (e) {
		const x = e.touches ? e.touches[0].clientX : e.clientX;
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		this.bounding = this.scope.canvas.draw.getBoundingClientRect();
		this.coord.x = (x - this.bounding.left) / this.bounding.width  * this.scope.width;
		this.coord.y = (y - this.bounding.top) / this.bounding.height * this.scope.height;
	}
}

class Clear extends DrawElement{
	constructor (parent,scope) {
		super();
		this.scope = scope;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-trash-can')
			.event('click',this.#clearHandler.bind(this))
			.appendTo(parent);
	}

	#clearHandler() {
		if (window.confirm('Do you really want to clear?')) {
			this.scope.drawPanel.history.execute(new CanvasDrawCommand(this.scope.canvas.draw,this.scope.context.draw));
			this.scope.context.draw.clearRect(0,0,this.scope.width,this.scope.height);
		}
	}

}

class History {
	constructor (parent,scope) {
		this.scope = scope;
		this.btn = new BaseElement()
			.addClass('paint__icon','icon-back')
			.event('click',this.undo.bind(this))
			.appendTo(parent);

		this.undos = [];
		this.limit = 10;
	}

	execute(cmd) {
		if (this.undos.length === this.limit) {
			this.undos = this.undos.splice(1);
		}
		this.undos.push( cmd );
		cmd.execute();
	}

	undo() {
		let cmd = null;

		if (this.undos.length > 0) {
			cmd = this.undos.pop();
		}

		if (cmd) {
			cmd.undo();
		}

		return cmd;
	}
}

class CanvasDrawCommand {
	constructor (canvas,ctx) {
		this.canvas = canvas;
		this.width = canvas.width;
		this.height = canvas.height;
		this.ctx = ctx;
		this.data = null;
	}

	execute() {
		this.data = this.canvas.toDataURL();
	}

	undo() {
		const img = new Image();
		img.onload = () => {
			this.ctx.clearRect(0,0,this.width,this.height);
			this.ctx.drawImage(img,0,0,this.width,this.height);
		};
		img.src = this.data;
	}

}

function createSettings (key,elements,defaultValue,iconFunction,callback) {
	const wrapper = new BaseElement()
		.addClass('paint__settings__container-wrapper')
		.DOMElement;

	const settings = new BaseElement()
		.addClass('paint__settings__container','hidden')
		.appendTo(wrapper);

	elements.forEach((size,index) => {
		const input = new BaseElement('input')
			.addAttribute('id',`${key}-${size}`)
			.addClass('paint__settings-input')
			.addAttribute('type','radio')
			.addAttribute('name',key)
			.addAttribute('value',size)
			.event('change',(e) => callback(Number(e.target.value)))
			.appendTo(settings.DOMElement);

		if (size === defaultValue) {
			input.addAttribute('checked',true)
		}

		new BaseElement('label')
			.addClass('paint__settings-label')
			.addAttribute('for',`${key}-${size}`)
			.innerHtml(iconFunction(size,index))
			.appendTo(settings.DOMElement)
			.DOMElement;

	});

	return {wrapper,settings};
}


function getFileName(url) {
	const uri = new URL(url);
	const splited = uri.pathname.split('/');
	const file = splited.find(name => name.match(/\./g));
	return file.split('.')[0];
}
